import { Splide, SplideSlide } from '@splidejs/react-splide';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

import ResponsiveImage from 'components/ResponsiveImage';
import VideoPlayer from 'components/VideoPlayer';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { breakpoints, cn } from 'utils/Tailwind';

import '@splidejs/react-splide/css';

export const OVERLAY_POSITIONS = {
  TOP: 'Top',
  CENTER: 'Center',
};

const CAROUSEL_OPTIONS = {
  type: 'loop',
  perPage: 1,
  focus: 'center',
  pagination: false,
  arrows: false,
  gap: '8px',
  speed: 400,
  easing: 'ease',
  waitForTransition: true,
  updateOnMove: true,
  mediaQuery: 'min',
  breakpoints: {
    0: {
      focus: 'center',
      padding: '24px',
    },
    [breakpoints.sm]: {
      focus: 'center',
      padding: '24vw',
    },
    [breakpoints.lg]: {
      focus: '0',
      perPage: 3,
      padding: 0,
      drag: false,
    },
  },
};

function PageSectionTriPanelModule({ items, isInverted, overlayPosition, trackCTAClick }) {
  const splideRef = useRef(null);

  return (
    <section className={cn('TriPanelModule mt-2 lg:m-10', [isInverted && 'text-white'])}>
      <Splide options={CAROUSEL_OPTIONS} ref={splideRef}>
        {items.map((card, index) => (
          <TriPanelCard
            key={`${card.asset.file.url}-${index}`}
            isInverted={isInverted}
            overlayPosition={overlayPosition}
            trackCTAClick={trackCTAClick}
            {...card}
          />
        ))}
      </Splide>
    </section>
  );
}

const TriPanelCard = React.memo(function TriPanelCard({
  asset,
  ctaLinks,
  eyebrow,
  isInverted,
  overlayPosition,
  title,
  subtitle,
  trackCTAClick,
}) {
  const isVideo = asset.file.contentType.includes('video');

  return (
    <SplideSlide className="Card group/card relative overflow-hidden">
      {isVideo ? (
        <VideoPlayer
          className="Video"
          aspectRatio={IMAGE_RATIOS.R_4x5}
          loop
          muted
          playing
          playsinline
          url={asset.file.url}
        />
      ) : (
        <ResponsiveImage
          alt={title}
          className="Image w-full transition-transform duration-300 ease-in-out will-change-transform lg:group-hover/card:scale-105"
          coverParentNode
          height={IMAGE_WIDTHS.W_2000}
          src={asset.file.url}
          width={IMAGE_WIDTHS.W_2000}
        />
      )}

      <div
        className={cn(
          'Content absolute inset-0 flex flex-col items-center gap-2 p-6 text-center md:px-8 md:py-16',
          'backface-hidden bg-black/20 will-change-transform',
          {
            'justify-start': overlayPosition === OVERLAY_POSITIONS.TOP,
            'justify-center': overlayPosition === OVERLAY_POSITIONS.CENTER,
          }
        )}
      >
        <TriPanelCardContent
          ctaLinks={ctaLinks}
          eyebrow={eyebrow}
          isInverted={isInverted}
          overlayPosition={overlayPosition}
          subtitle={subtitle}
          title={title}
          trackCTAClick={trackCTAClick}
        />
      </div>
    </SplideSlide>
  );
});

function TriPanelCardContent({
  ctaLinks,
  eyebrow,
  isInverted,
  overlayPosition,
  subtitle,
  title,
  trackCTAClick,
}) {
  return (
    <>
      {eyebrow && (
        <p className="Eyebrow text-sm font-medium uppercase tracking-widest">{eyebrow}</p>
      )}
      {title && <h2 className="text-lg/6 font-bold md:text-2xl/7">{title}</h2>}
      {subtitle && <p className="text-sm sm:text-base">{subtitle}</p>}
      {ctaLinks.length > 0 && (
        <div
          className={cn(
            'CTALinks absolute bottom-6 flex w-full flex-wrap justify-center gap-2 px-6 transition-opacity duration-300 ease-in-out focus-within:opacity-100 sm:px-6 lg:opacity-0 lg:group-hover/card:opacity-100',
            {
              'bottom-6 md:bottom-10 lg:bottom-6 xl:bottom-10':
                overlayPosition === OVERLAY_POSITIONS.TOP,
              'md:static md:mt-2': overlayPosition === OVERLAY_POSITIONS.CENTER,
            }
          )}
        >
          {ctaLinks.map(({ url, text }) => (
            <a
              key={text}
              href={url}
              className={cn(
                'CTALink flex-1 whitespace-pre border bg-transparent bg-white p-3 text-center text-[11px] font-medium uppercase tracking-widest text-black transition-colors duration-300 ease-in-out hover:bg-black hover:text-white sm:text-xs md:min-w-40 md:flex-none md:px-6',
                { 'text-black': !isInverted }
              )}
              onClick={() => trackCTAClick({ text, url })}
            >
              {text}
            </a>
          ))}
        </div>
      )}
    </>
  );
}

PageSectionTriPanelModule.propTypes = {
  isInverted: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object),
  overlayPosition: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
  trackCTAClick: PropTypes.func,
};

PageSectionTriPanelModule.defaultProps = {
  isInverted: true,
  items: [],
  overlayPosition: OVERLAY_POSITIONS.CENTER,
};

TriPanelCard.propTypes = {
  asset: PropTypes.shape({
    description: PropTypes.string,
    file: PropTypes.shape({
      url: PropTypes.string,
      contentType: PropTypes.string,
    }),
  }),
  ctaLinks: PropTypes.arrayOf(
    PropTypes.shape({
      linkUrl: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  eyebrow: PropTypes.string,
  isInverted: PropTypes.bool,
  overlayPosition: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  trackCTAClick: PropTypes.func,
  isActive: PropTypes.bool,
};

TriPanelCard.defaultProps = {
  ctaLinks: [],
  eyebrow: '',
  isInverted: false,
  overlayPosition: OVERLAY_POSITIONS.TOP,
  title: '',
  subtitle: '',
  isActive: false,
};

TriPanelCardContent.propTypes = {
  ctaLinks: PropTypes.array,
  eyebrow: PropTypes.string,
  isInverted: PropTypes.bool,
  overlayPosition: PropTypes.oneOf(Object.values(OVERLAY_POSITIONS)),
  subtitle: PropTypes.string,
  title: PropTypes.string,
  trackCTAClick: PropTypes.func,
};

TriPanelCardContent.defaultProps = {
  ctaLinks: [],
  eyebrow: '',
  isInverted: false,
  overlayPosition: OVERLAY_POSITIONS.TOP,
  subtitle: '',
  title: '',
};

export default PageSectionTriPanelModule;
